import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Box,
    Button,
    Grid,
    Paper,
    TextField,
    Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import { getRequest, putRequest } from "Includes/api/apicall";
import { GET_URL, PUT_URL } from "Includes/urls";

function editorIsEmpty(value) {
    const text = String(value || "")
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, " ")
        .trim();
    return !text;
}

const emptyForm = {
    module_name: "",
    url: "",
    text: "",
    link: "",
    link_text: "",
};

function FeatureAddEdit(props) {
    const { id } = useParams();
    const navigate = useNavigate();
    const [form, setForm] = useState(emptyForm);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState("");
    const [slides, setSlides] = useState([]);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        getRequest(`${GET_URL.feature_screens.api}${id}/`, {}, props)
            .then((response) => {
                if (response && response.status === 200) {
                    const row = response.data?.data || {};
                    setForm({
                        module_name: row.module_name || "",
                        url: row.url || "",
                        text: row.text || "",
                        link: row.link || "",
                        link_text: row.link_text || "",
                    });
                    setImagePreview(row.image_link || row.image || "");
                    const loaded = Array.isArray(row.dialog_slides) ? row.dialog_slides : [];
                    setSlides(loaded.map((slide) => ({
                        key: `slide-${slide.id}`,
                        id: slide.id,
                        kind: slide.kind === "image" ? "image" : "text",
                        text: slide.text || "",
                        file: null,
                        preview: slide.image_link || slide.image || "",
                    })));
                }
            })
            .finally(() => setLoading(false));
    }, [id, props]);

    const setField = (name) => (event) => {
        const value = event.target.value;
        setForm((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const onImageChange = (event) => {
        const file = event.target.files && event.target.files[0];
        setImageFile(file || null);
        if (file) {
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const addSlide = (kind) => {
        setSlides((prev) => [
            ...prev,
            { key: `new-${Date.now()}`, id: null, kind, text: "", file: null, preview: "" },
        ]);
    };

    const updateSlide = (key, patch) => {
        setSlides((prev) => prev.map((slide) => (slide.key === key ? { ...slide, ...patch } : slide)));
    };

    const removeSlide = (key) => {
        setSlides((prev) => prev.filter((slide) => slide.key !== key));
    };

    const validate = () => {
        const next = {};
        if (!form.module_name.trim()) next.module_name = "Enter module name";
        if (!form.url.trim()) next.url = "Enter URL";
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const save = () => {
        if (!validate()) return;
        const payload = new FormData();
        payload.append("module_name", form.module_name.trim());
        payload.append("url", form.url.trim());
        payload.append("text", form.text);
        payload.append("link", form.link.trim());
        payload.append("link_text", form.link_text.trim());
        if (imageFile) payload.append("image", imageFile);
        payload.append("dialog_slides", JSON.stringify(slides.map((slide) => ({
            id: slide.id,
            kind: slide.kind,
            text: slide.kind === "text" && !editorIsEmpty(slide.text) ? slide.text : "",
        }))));
        slides.forEach((slide, index) => {
            if (slide.kind === "image" && slide.file) {
                payload.append(`slide_image_${index}`, slide.file);
            }
        });

        setSaving(true);
        putRequest(`${PUT_URL.feature_screens.api}${id}/`, payload, { ...props, return_error: true })
            .then((response) => {
                if (response && (response.status === 200 || response.status === 201)) {
                    navigate("/basicdetails/feature-adds");
                }
            })
            .finally(() => setSaving(false));
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate("/basicdetails/feature-adds")}
                    sx={{ textTransform: "none" }}
                >
                    Back
                </Button>
                <Typography variant="h5" fontWeight={600}>
                    Edit screen
                </Typography>
            </Box>
            <Paper sx={{ mt: 3, p: 3, borderRadius: 3, maxWidth: 720 }}>
                {loading ? (
                    <Typography color="text.secondary">Loading...</Typography>
                ) : (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                label="Module name"
                                fullWidth
                                value={form.module_name}
                                error={Boolean(errors.module_name)}
                                helperText={errors.module_name}
                                onChange={setField("module_name")}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="URL"
                                fullWidth
                                value={form.url}
                                error={Boolean(errors.url)}
                                helperText={errors.url}
                                onChange={setField("url")}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Text"
                                fullWidth
                                multiline
                                minRows={3}
                                value={form.text}
                                onChange={setField("text")}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Button variant="outlined" component="label">
                                {imageFile ? imageFile.name : "Choose image"}
                                <input hidden accept="image/*" type="file" onChange={onImageChange} />
                            </Button>
                            {imagePreview ? (
                                <Box
                                    component="img"
                                    src={imagePreview}
                                    alt=""
                                    sx={{ display: "block", mt: 2, maxWidth: 280, maxHeight: 180, objectFit: "contain" }}
                                />
                            ) : null}
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 1 }}>
                                Dialog screens
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Add a text screen or an image screen. View details steps through them. The small popup stays unchanged.
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                                <Button variant="outlined" onClick={() => addSlide("text")}>Add text</Button>
                                <Button variant="outlined" onClick={() => addSlide("image")}>Add image</Button>
                            </Box>
                        </Grid>
                        {slides.map((slide, index) => (
                            <Grid item xs={12} key={slide.key}>
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                        <Typography variant="body2" fontWeight={600}>
                                            Screen {index + 1} · {slide.kind === "image" ? "Image" : "Text"}
                                        </Typography>
                                        <Button size="small" color="error" onClick={() => removeSlide(slide.key)}>
                                            Remove
                                        </Button>
                                    </Box>
                                    {slide.kind === "text" ? (
                                        <Box sx={{ "& .ql-container": { minHeight: 160 }, "& .ql-editor": { minHeight: 120 } }}>
                                            <ReactQuill
                                                theme="snow"
                                                value={slide.text || ""}
                                                onChange={(value) => updateSlide(slide.key, { text: value })}
                                                placeholder="Write dialog text"
                                            />
                                        </Box>
                                    ) : (
                                        <Box>
                                            <Button variant="outlined" component="label">
                                                {slide.file ? slide.file.name : "Choose image"}
                                                <input
                                                    hidden
                                                    accept="image/*"
                                                    type="file"
                                                    onChange={(event) => {
                                                        const file = event.target.files && event.target.files[0];
                                                        updateSlide(slide.key, {
                                                            file: file || null,
                                                            preview: file ? URL.createObjectURL(file) : slide.preview,
                                                        });
                                                    }}
                                                />
                                            </Button>
                                            {slide.preview ? (
                                                <Box
                                                    component="img"
                                                    src={slide.preview}
                                                    alt=""
                                                    sx={{ display: "block", mt: 2, maxWidth: 280, maxHeight: 180, objectFit: "contain" }}
                                                />
                                            ) : null}
                                        </Box>
                                    )}
                                </Paper>
                            </Grid>
                        ))}
                        <Grid item xs={12}>
                            <TextField
                                label="Link"
                                fullWidth
                                value={form.link}
                                onChange={setField("link")}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Link text"
                                fullWidth
                                value={form.link_text}
                                onChange={setField("link_text")}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Button onClick={() => navigate("/basicdetails/feature-adds")} disabled={saving} sx={{ mr: 1 }}>
                                Cancel
                            </Button>
                            <Button variant="contained" onClick={save} disabled={saving}>
                                {saving ? "Saving..." : "Save"}
                            </Button>
                        </Grid>
                    </Grid>
                )}
            </Paper>
        </Box>
    );
}

export default FeatureAddEdit;
